// OIDC RP-initiated logout page (#1901).
//
// A relying party sends the browser to `<issuer>/logout`; that endpoint
// validates what it was given, stashes it under a one-time request_id, and
// redirects here. This page is where the sign-out actually happens, because the
// Firebase session lives on this origin and only a page here can end it.
//
// It is also reachable with NO request_id at all, and that is deliberate: it is
// the only linkable sign-out URL we have. `<issuer>/logout` cannot be linked
// for that purpose — it answers 503 wherever OIDC is unconfigured, which is
// staging and production today — while this route is plain frontend and works
// in every environment.
//
// Routed unwrapped. Not `PublicRoute`, which bounces a signed-in user away
// before they can be signed out, and emphatically not `ProtectedRoute`, which
// renders a redirect the instant `user` goes null — that is to say, in the
// middle of the one thing this page does.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { appConfig } from '@/lib/env';
import { LoadingSpinner } from '@/components/ui';

type Phase = 'checking' | 'confirm' | 'working' | 'done' | 'error';
type Outcome = 'signed-out' | 'still-signed-in';

interface LogoutContext {
  clientName?: string;
  /** Subject of a VERIFIED id_token_hint. Absent when the relying party sent
   *  no hint, or one we could not verify. */
  sub?: string;
}

export function OidcLogoutPage() {
  const [searchParams] = useSearchParams();
  // `firebaseUser`, NOT `user`. `user` is the Firestore profile document, and
  // `loadUserProfile` returns null from its catch — a genuinely missing
  // document yields a synthesised profile instead. So a transient Firestore
  // read failure (an offline blip, a blocked `firestore.googleapis.com`, a
  // corporate proxy) leaves `user` null and `isLoading` false while the
  // Firebase session is very much alive.
  //
  // Deciding on `user` would then take the "nobody is signed in" branch:
  // consume the stash, skip the sign-out, return the user to the relying party
  // and tell them they are signed out — while their session with us stands.
  // That is the same silent failure the `isLoading` guard below exists to
  // prevent, reached by a different door. `AuthContext` already documents this
  // hazard for `refreshUserProfile`; the `onAuthStateChanged` path is
  // unguarded, and this page is the first place where the consequence is a
  // security one rather than a cosmetic one.
  //
  // The session is the thing being ended here, so the session is the thing to
  // read. `OidcContinuePage` reaches for `auth.currentUser` for the same reason.
  const { firebaseUser, isLoading, signOut } = useAuth();
  const [phase, setPhase] = useState<Phase>('checking');
  const [clientName, setClientName] = useState<string | undefined>();
  const [outcome, setOutcome] = useState<Outcome>('signed-out');
  const startedRef = useRef(false);

  const requestId = searchParams.get('request_id');

  /**
   * Consume the stash, then leave. Called from every terminal path — the user
   * confirmed, the user declined, or nobody needed asking.
   *
   * `doSignOut` is false on a decline, and a decline STILL completes and still
   * redirects. The relying party has already ended its own session; stranding
   * the user on our page helps nobody, and the relying party cannot tell a
   * decline from a completion in any case (which the partner guide says
   * plainly, because it is the kind of thing they would otherwise assume).
   */
  const finish = useCallback(
    async ({
      doSignOut,
      terminalOutcome,
    }: {
      doSignOut: boolean;
      terminalOutcome: Outcome;
    }) => {
      setPhase('working');

      if (doSignOut) {
        try {
          await signOut();
        } catch {
          // Must not fall through. Completing here would consume the stash and
          // return the user to the relying party as though they had signed
          // out, while their session with us stood — the single worst outcome
          // this page can produce, and a silent one.
          setPhase('error');
          return;
        }
      }

      // Only the server assembles the redirect URL. The page never sees the
      // target as parts to join, and never reads one off its own query string:
      // that is the entire reason the stash exists.
      if (requestId) {
        try {
          const response = await fetch(
            `${appConfig.api.baseUrl}/logout/complete`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ request_id: requestId }),
            }
          );
          if (response.ok) {
            const body = (await response.json()) as { redirectTo?: string };
            if (body.redirectTo) {
              window.location.assign(body.redirectTo);
              return;
            }
          }
        } catch {
          // Network failure only — `fetch` rejects on a dropped connection or
          // a CORS refusal, NOT on an HTTP error status. A stash that expired
          // while the prompt sat open comes back as a perfectly ordinary 400
          // and is handled by the `response.ok` test above falling through.
          //
          // Either way the sign-out already happened, which is the part that
          // matters; only the return trip to the relying party is lost.
        }
      }

      setOutcome(terminalOutcome);
      setPhase('done');
    },
    [requestId, signOut]
  );

  useEffect(() => {
    // `isLoading` is a WAIT, never a "no session". Firebase resolves auth
    // asynchronously, so acting here before it settles would read a signed-in
    // person as signed out, complete silently, and hand them back to the
    // relying party still logged in to us — exactly the failure this page
    // exists to prevent, with no symptom.
    if (isLoading) {
      return;
    }
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    const begin = async () => {
      let context: LogoutContext | null = null;

      if (requestId) {
        try {
          const response = await fetch(
            `${appConfig.api.baseUrl}/logout/context`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ request_id: requestId }),
            }
          );
          if (response.ok) {
            context = (await response.json()) as LogoutContext;
          }
        } catch {
          // Left null: handled as "we could not learn anything", below.
        }
      }

      setClientName(context?.clientName);

      if (!firebaseUser) {
        // Nothing to end. Complete and return them to the relying party
        // rather than showing a prompt to somebody who is already signed out.
        //
        // This is a deliberate departure from a MUST. Section 2 says the OP
        // must ask when the id_token does not belong to the current session,
        // and with no session at all a verified hint belongs to none. There is
        // no session to protect and no user to mislead, so asking would be
        // noise. Recorded as a departure, not an oversight.
        await finish({ doSignOut: false, terminalOutcome: 'signed-out' });
        return;
      }

      // A verified hint naming the person actually signed in here: the relying
      // party has proved it holds a token for them, so no confirmation is
      // needed and none is shown.
      if (context?.sub && context.sub === firebaseUser.uid) {
        await finish({ doSignOut: true, terminalOutcome: 'signed-out' });
        return;
      }

      // Everything else with a live session asks. That covers the hint that
      // was absent or unverifiable, the hint naming somebody OTHER than the
      // person signed in — a stale tab of theirs on a shared machine — a bare
      // link with no request_id, and a stash we could not read. In the last
      // case the redirect is not given up on: `finish` still calls
      // `/logout/complete`, which succeeds if the stash was readable after all.
      setPhase('confirm');
    };

    void begin();
  }, [isLoading, firebaseUser, requestId, finish]);

  if (isLoading || phase === 'checking' || phase === 'working') {
    return <LoadingSpinner fullScreen message="Signing you out..." />;
  }

  if (phase === 'confirm') {
    return (
      <LogoutShell
        heading="Sign out of HeapsterID?"
        message={
          clientName
            ? `${clientName} has asked us to sign you out of HeapsterID on this device.`
            : 'This will sign you out of HeapsterID on this device.'
        }
      >
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={() =>
              void finish({ doSignOut: true, terminalOutcome: 'signed-out' })
            }
            className="inline-flex justify-center rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Sign out
          </button>
          <button
            type="button"
            onClick={() =>
              void finish({
                doSignOut: false,
                terminalOutcome: 'still-signed-in',
              })
            }
            className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Stay signed in
          </button>
        </div>
      </LogoutShell>
    );
  }

  if (phase === 'error') {
    return (
      <LogoutShell
        heading="Sign-Out Could Not Be Completed"
        message="Something went wrong and you are still signed in to HeapsterID. Try again, or sign out from your dashboard."
      />
    );
  }

  return (
    <LogoutShell
      heading={
        outcome === 'signed-out'
          ? 'You are signed out of HeapsterID'
          : 'You are still signed in to HeapsterID'
      }
      message={
        outcome === 'signed-out'
          ? 'You can close this tab, or sign in again whenever you need to.'
          : 'Nothing was changed. You can close this tab.'
      }
    />
  );
}

function LogoutShell({
  heading,
  message,
  children,
}: {
  heading: string;
  message: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 leading-tight">
            {heading}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">{message}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
